import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import type { Heat } from '../../../types/heats';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heats: Heat[];
  /** `null` = tous les jours. */
  onExport: (date: string | null) => void;
}

/** Choix du jour à exporter en PDF (ou tout le planning). */
const ExportPdfDialog: React.FC<Props> = ({ open, onOpenChange, heats, onExport }) => {
  const { t } = useTranslation();
  const dates = Array.from(new Set(heats.map(h => h.date).filter(Boolean))).sort();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('heats.pdfDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2 py-4">
          <p className="text-sm text-muted-foreground mb-4">{t('heats.pdfDialog.selectDay')}</p>

          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => onExport(null)}
          >
            <CalendarIcon size={18} className="mr-2" />
            {t('heats.pdfDialog.allDays', { count: heats.length })}
          </Button>

          {dates.map(date => {
            const heatsForDate = heats.filter(h => h.date === date);
            const dateFormatted = date
              ? new Date(date).toLocaleDateString('fr-FR', {
                  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
                })
              : t('heats.pdfDialog.noDate');

            return (
              <Button
                key={date}
                variant="outline"
                className="w-full justify-start"
                onClick={() => onExport(date || null)}
              >
                <CalendarIcon size={18} className="mr-2" />
                <div className="text-left">
                  <div className="capitalize">{dateFormatted}</div>
                  <div className="text-xs text-muted-foreground">{heatsForDate.length} heat(s)</div>
                </div>
              </Button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportPdfDialog;
